import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

import { getArtistSession, getArtistAuthHeaders } from '../utils/artistSession';

const BillingContext = createContext(null);

const isPaymentMethodExpired = paymentMethod => {
  if (!paymentMethod?.expMonth || !paymentMethod?.expYear) {
    return false;
  }

  const now = new Date();
  return (
    paymentMethod.expYear < now.getFullYear() ||
    (paymentMethod.expYear === now.getFullYear() && paymentMethod.expMonth < now.getMonth() + 1)
  );
};

export function BillingProvider({ children }) {
  const auth0User = useSelector(state => state.authenticationSlice.auth0User);
  const isAdmin = useSelector(state => state.authenticationSlice.isAdmin);
  const userId = useMemo(() => getArtistSession(auth0User).userId, [auth0User]);
  const [loading, setLoading] = useState(Boolean(userId) && !isAdmin);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (isAdmin || !userId) {
      setSummary(null);
      setError(null);
      setLoading(false);
      return;
    }

    const headers = getArtistAuthHeaders();
    if (!headers.authorization) {
      setLoading(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/billing/summary`,
        { headers }
      );
      setSummary(response.data?.data || null);
    } catch (err) {
      setSummary(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId, isAdmin]);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => {
    const subscriptions = summary?.subscriptions || [];
    const hasPaymentIssue = subscriptions.some(
      item => item.status === 'past_due' || item.status === 'incomplete'
    );
    const expiredPaymentMethod = Boolean(
      summary?.hasSubscription &&
        summary?.defaultPaymentMethod?.last4 &&
        isPaymentMethodExpired(summary.defaultPaymentMethod)
    );
    const needsPaymentMethodUpdate =
      Boolean(summary?.hasSubscription && !summary?.defaultPaymentMethod?.last4) ||
      expiredPaymentMethod;

    return {
      userId,
      loading,
      summary,
      error,
      subscriptions,
      hasPaymentIssue,
      expiredPaymentMethod,
      needsPaymentMethodUpdate,
      reload,
    };
  }, [userId, loading, summary, error, reload]);

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within BillingProvider');
  }
  return context;
}
